import { useQuery } from '@apollo/client';
import { NftSort } from '@mogul/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { nftsApi } from '../api';
import { getDeletedNftIds } from '../api-helpers/nft';
import type { Asset } from '../lib/graph/MogulMarketplace';
import assetQuery from '../lib/graph/queries/assetQuery';
import { useWeb3State } from '.';
import type { Nft } from './nft-api.hook';

interface PaginationProps {
  step?: number;
  first?: number;
}

export interface AugmentedAsset extends Asset {
  chainID: string | undefined;
}

export const useMarketplaceScroll = (
  nftContractAddress: string,
  paginationOptions?: PaginationProps | undefined,
) => {
  const { walletDetails } = useWeb3State();
  const [skip, setSkip] = useState(0);
  const [first, _] = useState(paginationOptions?.first ?? 20);
  const [ended, setEnded] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchedData, setFetchedData] = useState<AugmentedAsset[]>([]);

  // Create hook
  const assetQueryData = useQuery<{ asset?: Asset }>(assetQuery);

  const assetsFromNfts = async (nfts: Nft[]) => {
    const promises = [];
    for (const nft of nfts) {
      promises.push(
        assetQueryData.fetchMore({
          variables: { graphId: `${nft.contractAddress}/${nft.id}` },
          context: { network: nft.chainId },
        }),
      );
    }

    const assets: AugmentedAsset[] = [];
    const GQLResults = await Promise.all(promises);
    GQLResults.forEach((data, i) => {
      if (data.data.asset !== undefined) {
        const asset = { ...data.data.asset, chainID: nfts[i].chainId };
        assets.push(asset);
      }
    });
    return assets;
  };

  const listMintedNFTs = async () => {
    if (ended || nftContractAddress === '') {
      return;
    }

    // DB list
    const result = await nftsApi.listMintedNfts({
      limit: first,
      offset: skip,
      sort: NftSort.TitleDesc,
      network: [walletDetails.chain.type],
      // contractAddress: nftContractAddress.toLowerCase(),
    });
    const nfts = result.data.data;

    const assets = await assetsFromNfts(nfts);

    if (assets.length === 0) {
      setEnded(true);
      setSkip(0);
    } else {
      setFetchedData(fetchedData.concat(assets));
    }
    setFetching(false);
  };

  // List once on load
  useEffect(() => {
    void (async () => {
      await listMintedNFTs();
    })();
  }, []);

  // List when scrolled to bottom of screen
  useEffect(() => {
    if (!fetching) {
      return;
    }

    setSkip(prev => prev + first);

    void (async () => {
      await listMintedNFTs();
    })();
  }, [fetching]);

  const loader = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (
      entries: {
        isIntersecting: boolean;
      }[],
    ) => {
      const target: { isIntersecting: boolean } = entries[0];
      if (target.isIntersecting) {
        setFetching(true);
      }
    },
    [],
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) {
      observer.observe(loader.current);
    }
  }, [handleObserver]);

  setLoading(false);

  return {
    loading,
    fetchedData,
    loader,
  };
};

export const useExcludedAssetsIds = (
  nftContractAddress: string | null | undefined,
) => {
  const [excludedAssetsIds, setExcludedAssetsIds] =
    useState<string[] | null>(null);

  useEffect(() => {
    const fetchExcludedAssetIds = async () => {
      if (nftContractAddress != null) {
        const ids = await getDeletedNftIds(nftContractAddress);
        setExcludedAssetsIds(
          ids.map(id => `${nftContractAddress.toLowerCase()}/${id}`),
        );
      } else {
        setExcludedAssetsIds([]);
      }
    };

    void fetchExcludedAssetIds();
  }, [setExcludedAssetsIds, nftContractAddress]);

  return excludedAssetsIds;
};
